import React, { useContext, useEffect, useState } from "react";
import Select from "react-select";
import { AuthContext } from "../context/AuthContext";
import { canAccessAdminPanel } from "../utils/adminAccess";

const Admin = () => {
  const API_BASE = "https://api.cloud.linkbms.eu";
  const { user } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [userDevices, setUserDevices] = useState({});
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [deviceDescription, setDeviceDescription] = useState(""); // New state for device description
  const [publicDescription, setPublicDescription] = useState(""); // New state for device description
  const [isPublic, setIsPublic] = useState(false); // New state for 'public' field
  const [error, setError] = useState("");
  const [deviceIdHex, setDeviceIdHex] = useState("");

  const [files, setFiles] = useState([]); // New state for storing files
  const [selectedFilename, setSelectedFilename] = useState(""); // New state for selected file

  const [responseMessage, setResponseMessage] = useState("");
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState("");

  const formatNumber = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) {
      return "0";
    }
    return number.toLocaleString();
  };

  const formatBytes = (bytes) => {
    const value = Number(bytes);
    if (!Number.isFinite(value) || value <= 0) {
      return "0 B";
    }
    const units = ["B", "KB", "MB", "GB", "TB"];
    const index = Math.min(
      Math.floor(Math.log(value) / Math.log(1024)),
      units.length - 1
    );
    const scaled = value / 1024 ** index;
    return `${scaled.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
  };

  const formatDateTime = (value) => {
    if (!value) {
      return "-";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return date.toLocaleString();
  };

  const fetchAdminStats = async () => {
    try {
      setStatsLoading(true);
      setStatsError("");
      const response = await fetch(`${API_BASE}/admin/stats`, {
        method: "GET",
        credentials: "include",
      });

      const data = await response.json();
      if (response.ok) {
        setStats(data);
      } else {
        setStatsError(data.error || "Failed to fetch admin statistics");
      }
    } catch (err) {
      setStatsError("Error fetching statistics: " + err.message);
    } finally {
      setStatsLoading(false);
    }
  };

  const sendCommand = async (deviceId, command, value) => {
    try {
      const response = await fetch(`${API_BASE}/commandadmin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          device_id: deviceId,
          command: command,
          value: value,
        }),
        credentials: "include"
      });

      const result = await response.json();
      if (result.success) {
        setResponseMessage(result.message);
      } else {
        setResponseMessage("Failed to send command");
      }
    } catch (error) {
      console.error("Error sending command:", error);
      setResponseMessage("Error sending command");
    }
  };
  // Fetch all users and devices initially
  const fetchUsersAndDevices = async () => {
    try {
      const response = await fetch(`${API_BASE}/get_users_and_devices`, {
        method: "GET",
        credentials: "include"
      });

      const data = await response.json();
      if (response.ok) {
        const sortedUsers = data.users.sort((a, b) => {
          const nameA = a.name || ""; // Default to empty string if name is undefined
          const nameB = b.name || "";
          return nameA.localeCompare(nameB);
        });

        // Sort devices by device[2] (assuming it's a string or number and that devices is an array)
        const sortedDevices = data.devices.sort((a, b) => {
          const aValue = a[2] || ""; // Default value if undefined
          const bValue = b[2] || "";
          if (aValue < bValue) return -1;
          if (aValue > bValue) return 1;
          return 0;
        });

        setUsers(sortedUsers);
        setDevices(sortedDevices);
        setUserDevices(data.user_devices);
      } else {
        setError(data.error || "Failed to fetch data");
      }

      // Fetch files separately
      const filesResponse = await fetch(`${API_BASE}/files_list`, {
        method: "GET",
        credentials: "include"
      });
      const filesData = await filesResponse.json();
      if (filesResponse.ok) {
        setFiles(filesData);
      } else {
        setError(filesData.error || "Failed to fetch files");
      }
    } catch (err) {
      setError("Error fetching data: " + err.message);
    }
  };

  const refreshAdminData = async () => {
    await Promise.all([fetchUsersAndDevices(), fetchAdminStats()]);
  };

  // Handle form submission for adding a new device to a user
  const handleAddDevice = async (event) => {
    event.preventDefault();

    try {
      const response = await fetch(`${API_BASE}/add_device`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: selectedUserId,
          device_id: selectedDeviceId,
        }),
        credentials: "include"
      });

      const data = await response.json();
      if (response.ok) {
        alert("Device added successfully to user.");
        refreshAdminData(); // Refresh data
        setSelectedDeviceId("");
      } else {
        alert("Error: " + data.error);
      }
    } catch (err) {
      alert("Error adding device: " + err.message);
    }
  };

  // Handle form submission for removing a device from a user
  const handleRemoveDevice = async (deviceId) => {
    try {
      const response = await fetch(`${API_BASE}/remove_device`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ user_id: selectedUserId, device_id: deviceId }),
        credentials: "include"
      });

      const data = await response.json();
      if (response.ok) {
        alert("Device removed successfully from user.");
        refreshAdminData(); // Refresh data
      } else {
        alert("Error: " + data.error);
      }
    } catch (err) {
      alert("Error removing device: " + err.message);
    }
  };

  // Handle updating the device description and public status
  const handleUpdateDevice = async () => {
    if (!selectedDeviceId) {
      alert("Please select a device to update.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/update_device`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          device_id: selectedDeviceId,
          description: deviceDescription,
          public_description: publicDescription,
          public: isPublic,
        }),
        credentials: "include"
      });

      const data = await response.json();
      if (response.ok) {
        alert("Device updated successfully.");
        refreshAdminData(); // Refresh data
      } else {
        alert("Error: " + data.error);
      }
    } catch (err) {
      alert("Error updating device: " + err.message);
    }
  };

  // When a user is selected, fetch their devices
  const userOptions = users.map((user) => ({
    value: String(user[0]),
    label: `${user[1]} (${user[2]})`,
  }));

  // Needed to reflect external changes to selectedUserId (i.e. after deleting a user)
  const selectedUserOption =
    userOptions.find((option) => option.value === String(selectedUserId)) || null;

  const handleUserSelect = (selection) => {
    const userId = selection?.value ?? "";
    setSelectedUserId(userId);
  };

  // Handle device selection and load the selected device's details
  const deviceOptions = devices.map((device) => ({
    value: String(device[0]),
    label: `${device[2]} (${device[1]} FW:${device[5]} SFW:${device[6]})`,
  }));

  // Needed to reflect external changes to selectedDeviceId (i.e. after adding a device to a user)
  const selectedDeviceOption = deviceOptions.find(
    (option) => option.value === String(selectedDeviceId)
  ) || null;

  const loginDailyRows = stats?.daily?.logins || [];
  const visitDailyRows = stats?.daily?.device_visits || [];
  const visitRowsByDay = visitDailyRows.reduce((accumulator, row) => {
    accumulator[row.day] = row;
    return accumulator;
  }, {});
  const allDailyDays = [...new Set([
    ...loginDailyRows.map((row) => row.day),
    ...visitDailyRows.map((row) => row.day),
  ])].sort();

  const handleDeviceSelect = (selection) => {
    const deviceId = selection?.value ?? "";
    setSelectedDeviceId(deviceId);

    const selectedDevice = devices.find(
      (device) => String(device[0]) === String(deviceId)
    );
    if (selectedDevice) {
      setDeviceIdHex(selectedDevice[1]); // Assuming description is in the third field
      setDeviceDescription(selectedDevice[2]); // Assuming description is in the third field
      setIsPublic(selectedDevice[3]); // Assuming public status is in the fourth field
      setPublicDescription(selectedDevice[4]); // Assuming public status is in the fourth field
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_BASE}/device/delete_history?device_id=${selectedDeviceId}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include"
        }
      );

      const data = await response.json();
      if (response.ok) {
        alert("History deleted");
      } else {
        alert("Error: " + data.error);
      }
    } catch (err) {
      alert("An error occurred while deleting the device data: " + err.message);
    }
  };

  const handleDeleteDevice = async () => {
    if (!selectedDeviceId) {
      alert("Please select a device to delete.");
      return;
    }

    const selectedDevice = devices.find(
      (device) => String(device[0]) === String(selectedDeviceId)
    );
    const selectedDeviceName = selectedDevice
      ? `${selectedDevice[2]} (${selectedDevice[1]})`
      : `ID ${selectedDeviceId}`;

    if (
      !window.confirm(
        `Delete device ${selectedDeviceName}? This will permanently remove it from devices, devicelist, and related data.`
      )
    ) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/delete_device`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ device_id: selectedDeviceId }),
        credentials: "include",
      });

      const data = await response.json();
      if (response.ok) {
        alert("Device deleted successfully.");
        setSelectedDeviceId("");
        setDeviceIdHex("");
        setDeviceDescription("");
        setPublicDescription("");
        setIsPublic(false);
        await refreshAdminData();
      } else {
        alert("Error: " + (data.error || "Failed to delete device"));
      }
    } catch (err) {
      alert("Error deleting device: " + err.message);
    }
  };

  const handleDescriptionChange = (e) => {
    setDeviceDescription(e.target.value);
  };

  const handlePublicDescriptionChange = (e) => {
    setPublicDescription(e.target.value);
  };

  // Handle public checkbox change
  const handlePublicChange = (e) => {
    setIsPublic(e.target.checked);
  };

  const handleUpdateFilesystem = async () => {
    if (!selectedFilename) {
      alert("Please select a file to generate a download link.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/updatefs/${selectedDeviceId}/${selectedFilename}`,
        {
          method: "POST",
          credentials: "include"
        }
      );

      const data = await response.json();
      if (response.ok) {
        alert("UpdateFS sent");
      } else {
        alert("Error: " + data.error);
      }
    } catch (err) {
      alert("Error generating download link: " + err.message);
    }
  };

  const handleUpdateFirmware = async () => {
    if (!selectedFilename) {
      alert("Please select a file to generate a download link.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/update/${selectedDeviceId}/${selectedFilename}`,
        {
          method: "POST",
          credentials: "include"
        }
      );

      const data = await response.json();
      if (response.ok) {
        alert("Update firmware sent");
      } else {
        alert("Error: " + data.error);
      }
    } catch (err) {
      alert("Error generating download link: " + err.message);
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    setSelectedFilename(e.target.value);
  };

  // Initial fetch of users and devices on component mount
  useEffect(() => {
    refreshAdminData();
  }, []);

  const handleDeleteUser = async () => {
    if (!selectedUserId) {
      alert("Please select a user to delete.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this user?")) {
      return; // Exit if user cancels the confirmation dialog
    }

    try {
      const response = await fetch(`${API_BASE}/delete_user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ user_id: selectedUserId }),
        credentials: "include"
      });

      const data = await response.json();
      if (response.ok) {
        alert("User deleted successfully.");
        refreshAdminData(); // Refresh the list of users, devices and stats
        setSelectedUserId(""); // Clear the selection
      } else {
        alert("Error: " + data.error);
      }
    } catch (err) {
      alert("Error deleting user: " + err.message);
    }
  };

  return canAccessAdminPanel(user) ? (
    <div className="admin-panel">
      <h1>Admin Panel</h1>

      {/* Form to select a user and device */}
      <form id="selectUserDeviceForm" onSubmit={handleAddDevice}>
        <table style={{ overflow: "visible" }}>
          <tbody>
            <tr>
              <td>
                <label htmlFor="userSelect">Select User:</label>
              </td>
              <td>
                <Select
                  inputId="userSelect"
                  name="user_id"
                  value={selectedUserOption}
                  onChange={handleUserSelect}
                  options={userOptions}
                  placeholder="Select user"
                  isClearable
                />
              </td>
            </tr>

            <tr>
              <td>
                <label htmlFor="deviceSelect">Select Device:</label>
              </td>
              <td>
                <Select
                  inputId="deviceSelect"
                  name="device_id"
                  value={selectedDeviceOption}
                  onChange={handleDeviceSelect}
                  options={deviceOptions}
                  placeholder="Select device"
                  isClearable
                />
              </td>
            </tr>

            <tr>
              <td colSpan="2">
                <input type="submit" value="Add Device to User" />
                <button
                  type="button"
                  style={{ marginLeft: "10px" }}
                  onClick={handleDeleteDevice}
                >
                  Remove Device
                </button>
                <button
                  type="button"
                  style={{ marginLeft: "10px", float: "right" }}
                  onClick={handleDeleteUser}
                >
                  Delete User
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </form>

      {/* Form to update the selected device's description and public status */}
      {selectedDeviceId && (
        <div>
          <h2>Edit Selected Device</h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleUpdateDevice();
            }}
          >
            <table>
              <tbody>
                <tr>
                  <td>
                    <label htmlFor="deviceDescription">
                      Device Description:
                    </label>
                  </td>
                  <td>
                    <input
                      id="deviceDescription"
                      type="text"
                      value={deviceDescription}
                      onChange={handleDescriptionChange}
                      placeholder="New Description"
                      required
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <label htmlFor="publicDescription">
                      Public Description:
                    </label>
                  </td>
                  <td>
                    <input
                      id="publicDescription"
                      type="text"
                      value={publicDescription}
                      onChange={handlePublicDescriptionChange}
                      placeholder="New Description"
                      required
                    />
                  </td>
                </tr>

                <tr>
                  <td>
                    <label htmlFor="publicCheckbox">Public:</label>
                  </td>
                  <td>
                    <input
                      id="publicCheckbox"
                      type="checkbox"
                      checked={isPublic}
                      onChange={handlePublicChange}
                    />
                  </td>
                </tr>

                <tr>
                  <td colSpan="2">
                    <button type="submit">Update Device</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </form>
          <div>
            <button onClick={() => sendCommand(deviceIdHex, "ZeroReset", "1")}>
              Zero reset + cycle counter
            </button>
            <button onClick={() => sendCommand(deviceIdHex, "Restart", "1")}>
              Restart GUI
            </button>
            <button onClick={() => sendCommand(deviceIdHex, "RestartSTM", "1")}>
              Restart BMS
            </button>
            <button
              onClick={() => sendCommand(deviceIdHex, "ForceCharge", "1")}
            >
              ForceCharge
            </button>
            <button onClick={() => sendCommand(deviceIdHex, "LemLow", "1")}>
              Calibrate LEM 0A
            </button>
          </div>
          {responseMessage && <p>{responseMessage}</p>}
        </div>
      )}

      {/* Display the selected user's devices */}
      {selectedUserId && (
        <div>
          <h2>{`Devices for Selected User`}</h2>
          {userDevices[selectedUserId]?.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Device Name</th>
                  <th>Description</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {userDevices[selectedUserId].map((deviceId) => {
                  const device = devices.find((dev) => dev[0] === deviceId);
                  return (
                    <tr key={deviceId}>
                      <td>{device ? device[1] : `Device ID: ${deviceId}`}</td>
                      <td>{device ? device[2] : "N/A"}</td>
                      <td>
                        <button onClick={() => handleRemoveDevice(deviceId)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p>No devices found for this user.</p>
          )}
        </div>
      )}

      <h2>Update Device</h2>
      {/* File selection and download link generation */}
      <div>
        <form>
          <table>
            <tbody>
              <tr>
                <td>
                  <label htmlFor="fileSelect">Select File:</label>
                </td>
                <td colSpan="2">
                  <select
                    id="fileSelect"
                    name="file_id"
                    value={selectedFilename}
                    onChange={handleFileSelect}
                    required
                  >
                    <option value="" disabled>
                      Select a file
                    </option>
                    {files.map((file) => (
                      <option key={file.filename} value={file.filename}>
                        {`${file.filename}`}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>

              <tr>
                <td>
                  <button type="button" onClick={handleUpdateFilesystem}>
                    Update filesystem
                  </button>
                </td>
                <td>
                  <button type="button" onClick={handleUpdateFirmware}>
                    Update firmware
                  </button>
                </td>

                <td>
                  <button type="button" onClick={handleDelete}>
                    Delete History
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </form>
      </div>

      {error && <p style={{ color: "red" }}>{error}</p>}

      <section className="admin-stats-dashboard">
        <div className="admin-stats-header">
          <h2>Cloud Statistics</h2>
          <p>Live overview of user activity, device engagement, and database usage.</p>
        </div>

        {statsLoading && <p>Loading statistics...</p>}
        {statsError && <p className="admin-stats-error">{statsError}</p>}

        {stats && (
          <>
            <div className="admin-stats-grid">
              <article className="admin-stats-card">
                <h3>Cloud Entries</h3>
                <p className="admin-stats-value">
                  {formatNumber(stats.engagement?.cloud_entries_total)}
                </p>
                <p className="admin-stats-meta">
                  7d: {formatNumber(stats.engagement?.cloud_entries_7d)} | Unique users (7d): {formatNumber(stats.engagement?.unique_users_7d)}
                </p>
              </article>

              <article className="admin-stats-card">
                <h3>Device Visits</h3>
                <p className="admin-stats-value">
                  {formatNumber(stats.engagement?.device_visits_total)}
                </p>
                <p className="admin-stats-meta">
                  Unique devices: {formatNumber(stats.engagement?.unique_devices_visited_total)} | User-device pairs: {formatNumber(stats.engagement?.unique_user_device_pairs)}
                </p>
              </article>

              <article className="admin-stats-card">
                <h3>Database Usage</h3>
                <p className="admin-stats-value">
                  {formatBytes(stats.usage?.database_total_bytes)}
                </p>
                <p className="admin-stats-meta">
                  Telemetry data: {formatBytes(stats.usage?.telemetry_estimated_bytes)}
                </p>
              </article>

              <article className="admin-stats-card">
                <h3>Platform Overview</h3>
                <p className="admin-stats-value">
                  {formatNumber(stats.overview?.devices_count)} devices
                </p>
                <p className="admin-stats-meta">
                  Users: {formatNumber(stats.overview?.users_count)} | Active 24h: {formatNumber(stats.overview?.active_devices_24h)}
                </p>
              </article>
            </div>

            <div className="admin-stats-split">
              <div className="admin-stats-panel">
                <h3>Top Visited Devices</h3>
                {stats.top?.visited_devices?.length ? (
                  <table>
                    <thead>
                      <tr>
                        <th>Device</th>
                        <th>Visits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.top.visited_devices.map((item) => (
                        <tr key={`device-visit-${item.device_id}`}>
                          <td>{item.description || item.device_id}</td>
                          <td>{formatNumber(item.visits)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>No device visits tracked yet.</p>
                )}
              </div>

              <div className="admin-stats-panel">
                <h3>Top Site Visitors</h3>
                {stats.top?.site_visitors?.length ? (
                  <table>
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Visits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.top.site_visitors.map((item) => (
                        <tr key={`site-visitor-${item.username}`}>
                          <td>{item.username}</td>
                          <td>{formatNumber(item.visits)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>No site visit activity tracked yet.</p>
                )}
              </div>
            </div>

            <div className="admin-stats-panel">
              <h3>Update History</h3>
              {stats.updates?.history?.length ? (
                <table>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>User</th>
                      <th>Role</th>
                      <th>Device</th>
                      <th>Action</th>
                      <th>File</th>
                      <th>Target</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.updates.history.map((item) => (
                      <tr key={`update-history-${item.id}`}>
                        <td>{formatDateTime(item.created_at)}</td>
                        <td>{item.username || "-"}</td>
                        <td>{item.role || "-"}</td>
                        <td>{item.device_code || "-"}</td>
                        <td>{item.action || "-"}</td>
                        <td>{item.file_name || "-"}</td>
                        <td>{item.target_version ?? "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No update actions tracked yet.</p>
              )}
            </div>

            <div className="admin-stats-split">
              <div className="admin-stats-panel">
                <h3>Activity Last 7 Days</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Day</th>
                      <th>Cloud Entries</th>
                      <th>Device Visits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allDailyDays.map((day) => {
                      const loginMatch = loginDailyRows.find((row) => row.day === day);
                      const visitMatch = visitRowsByDay[day];
                      return (
                        <tr key={`daily-${day}`}>
                          <td>{day}</td>
                          <td>{formatNumber(loginMatch?.entries || 0)}</td>
                          <td>{formatNumber(visitMatch?.visits || 0)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="admin-stats-panel">
                <h3>Biggest Database Tables</h3>
                {stats.usage?.table_sizes?.length ? (
                  <table>
                    <thead>
                      <tr>
                        <th>Table</th>
                        <th>Size</th>
                        <th>Rows (est.)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.usage.table_sizes.map((table) => (
                        <tr key={`table-size-${table.table_name}`}>
                          <td>{table.table_name}</td>
                          <td>{formatBytes(table.total_bytes)}</td>
                          <td>{formatNumber(table.estimated_rows)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>Table usage data not available.</p>
                )}
              </div>
            </div>

            <p className="admin-stats-footer">
              Last cloud entry: {formatDateTime(stats.engagement?.last_entry_at)} | Last device visit: {formatDateTime(stats.engagement?.last_visit_at)}
            </p>
          </>
        )}
      </section>
    </div>
  ) : (
    <div className="admin-panel">
      <h1>Access denied</h1>
    </div>
  );
};

export default Admin;
